// client/src/pages/FAQ.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const faqs = [
  {
    category: 'General',
    items: [
      {
        q: 'What is BioMedArticles?',
        a: 'BioMedArticles is a search platform that gives you a faster, cleaner way to explore ClinicalTrials.gov — the U.S. government\'s official registry of 574,000+ clinical studies from around the world. We provide real-time data directly from the ClinicalTrials.gov API with an improved search interface.'
      },
      {
        q: 'Where does the data come from?',
        a: 'All data comes directly from the ClinicalTrials.gov API v2, maintained by the U.S. National Library of Medicine. We do not modify, filter, or delay any data — every search returns live results in real time.'
      },
      {
        q: 'Is BioMedArticles affiliated with ClinicalTrials.gov or the U.S. government?',
        a: 'No. BioMedArticles is an independent platform. We use the ClinicalTrials.gov public API to surface data. We are not affiliated with the NIH, NLM, or any government agency.'
      },
      {
        q: 'Is my data private?',
        a: 'Yes. We do not sell your data, share it with advertisers, or log your specific searches to any third-party service. Your account information is stored securely and your searches are only used to power your results.'
      }
    ]
  },
  {
    category: 'Searching',
    items: [
      {
        q: 'How do I find studies for a specific condition?',
        a: 'Type the condition name in the search bar. Use the autocomplete suggestions to select an exact term — this improves result accuracy. For example, type "diab" and select "Diabetes Mellitus Type 2" from the dropdown.'
      },
      {
        q: 'Can I search by NCT ID?',
        a: 'Yes. If you already have an NCT number (e.g. NCT04280705), paste it directly into the search bar and press Search. You\'ll be taken directly to that study\'s detail page.'
      },
      {
        q: 'What do the status labels mean?',
        a: 'RECRUITING means the study is actively enrolling. NOT YET RECRUITING means enrollment hasn\'t opened yet. COMPLETED means the study has finished. TERMINATED means it was stopped early. WITHDRAWN means it was cancelled before enrolling anyone.'
      },
      {
        q: 'What is an Essie expression?',
        a: 'Essie is ClinicalTrials.gov\'s native query language for advanced filtering. For example, AREA[MinimumAge]RANGE[MIN, 18 years] finds studies accepting participants under 18. This feature is available to Pro subscribers.'
      },
      {
        q: 'Why does my search return so many results?',
        a: 'The database contains over 574,000 studies. If your search is too broad, use the sidebar filters to narrow by status (e.g. RECRUITING only), phase, location, or date range. You can also combine multiple filters at once.'
      },
      {
        q: 'Can I search for studies in other countries?',
        a: 'Yes. Use the Location filter to search by city, state, or country. The database includes studies from 50+ countries worldwide.'
      }
    ]
  },
  {
    category: 'Account & Access',
    items: [
      {
        q: 'Is registration required?',
        a: 'Yes, a free account is required to search and view studies. Registration is fast, free, and only requires your name, email, and a password. We send a 6-digit verification code to confirm your email.'
      },
      {
        q: 'What does the free plan include?',
        a: 'Free users get full search access across all 574,000+ studies, all filters, complete study detail pages, and the ability to save up to 5 studies.'
      },
      {
        q: 'I didn\'t receive my verification email. What do I do?',
        a: 'Check your spam or junk folder first. The email comes from support@biomedarticles.com. If it still doesn\'t arrive after a few minutes, you can request a new code from the verification page.'
      },
      {
        q: 'How do I reset my password?',
        a: 'Click "Forgot password?" on the login page, enter your email address, and we\'ll send you a password reset link. The link expires after 1 hour.'
      }
    ]
  },
  {
    category: 'Pro Subscription',
    items: [
      {
        q: 'What does Pro include?',
        a: 'Pro subscribers get unlimited saved studies, CSV export (up to 1,000 studies per search), up to 20 saved search presets, and access to advanced Essie expression filtering. Pro costs $9/month or $99/year.'
      },
      {
        q: 'How does CSV export work?',
        a: 'Click the "Export CSV" button on the search results page while logged in with a Pro account. The export uses your current search query and filters, and downloads a CSV file with up to 1,000 studies including NCT ID, title, status, phase, sponsor, conditions, and more.'
      },
      {
        q: 'Can I cancel my Pro subscription?',
        a: 'Yes, you can cancel anytime from the Profile → Subscription tab. Your Pro access continues until the end of your current billing period. You won\'t be charged again after cancellation.'
      },
      {
        q: 'How do I manage my billing?',
        a: 'Go to Profile → Subscription and click "Manage Billing". This opens the Stripe billing portal where you can update your payment method, view invoices, or change your plan.'
      },
      {
        q: 'Is there a free trial for Pro?',
        a: 'We don\'t currently offer a free trial, but the free plan gives you full search access to evaluate the platform before upgrading. We may add a trial period in future.'
      }
    ]
  },
  {
    category: 'Data & Clinical Trials',
    items: [
      {
        q: 'Can I participate in a clinical trial I find on BioMedArticles?',
        a: 'BioMedArticles is a search and research tool, not a recruitment platform. If you find a study you\'re interested in, use the contact information or ClinicalTrials.gov link on the study detail page to reach out directly to the study team.'
      },
      {
        q: 'How current is the data?',
        a: 'The data is live — every search queries the ClinicalTrials.gov API directly, so you always see the most current information available on the official registry.'
      },
      {
        q: 'What does "Has Results" mean?',
        a: 'Some completed studies post their outcome data back to ClinicalTrials.gov. If a study shows a "Results" badge, it means summary results are available. You can view these via the Results tab on the study detail page or the direct link to ClinicalTrials.gov.'
      },
      {
        q: 'Are all studies safe or endorsed by BioMedArticles?',
        a: 'No. BioMedArticles does not review, endorse, or evaluate any clinical study. Always consult with a qualified healthcare provider before participating in any clinical trial. The U.S. government does not review or approve the safety and science of all studies listed on ClinicalTrials.gov.'
      }
    ]
  }
];

function FAQItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      style={{ borderBottom: '1px solid var(--line)', cursor: 'pointer' }}
      onClick={() => setOpen(o => !o)}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 0', gap: 16 }}>
        <span style={{ fontSize: 15, fontWeight: 600, lineHeight: 1.4, flex: 1, color: 'var(--ink)' }}>{q}</span>
        <span style={{ fontSize: 18, color: 'var(--muted-2)', flexShrink: 0, transition: 'transform 0.2s', transform: open ? 'rotate(45deg)' : 'none' }}>+</span>
      </div>
      {open && (
        <div style={{ paddingBottom: 18, fontSize: 14, color: 'var(--muted)', lineHeight: 1.75 }}>
          {a}
        </div>
      )}
    </div>
  );
}

export default function FAQ() {
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState('All');
  const [search, setSearch] = useState('');

  const categories = ['All', ...faqs.map(f => f.category)];

  const filtered = faqs
    .filter(group => activeCategory === 'All' || group.category === activeCategory)
    .map(group => ({
      ...group,
      items: group.items.filter(item =>
        !search || item.q.toLowerCase().includes(search.toLowerCase()) || item.a.toLowerCase().includes(search.toLowerCase())
      )
    }))
    .filter(group => group.items.length > 0);

  return (
    <div style={{ minHeight: '100vh', background: 'var(--paper)' }}>
      {/* Hero */}
      <div style={{ background: 'var(--ink)', color: 'var(--paper)', padding: '64px 24px 56px', textAlign: 'center' }}>
        <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: '3px', color: 'var(--muted-2)', textTransform: 'uppercase', marginBottom: 16 }}>
          Support
        </div>
        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(32px, 5vw, 52px)', lineHeight: 1.08, marginBottom: 16 }}>
          Frequently Asked Questions
        </h1>
        <p style={{ color: 'var(--muted-2)', fontSize: 16, maxWidth: 500, margin: '0 auto 32px', lineHeight: 1.7 }}>
          Everything you need to know about BioMedArticles.
        </p>
        {/* Search */}
        <div style={{ maxWidth: 480, margin: '0 auto', position: 'relative' }}>
          <input
            style={{ width: '100%', padding: '14px 48px 14px 18px', background: 'var(--ink-2)', border: '1px solid var(--muted)', borderRadius: 0, color: 'var(--paper)', fontSize: 15, outline: 'none', fontFamily: 'var(--font-body)', boxSizing: 'border-box' }}
            placeholder="Search questions..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <span style={{ position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)', color: 'var(--muted-2)', fontSize: 16 }}>🔍</span>
        </div>
      </div>

      <div style={{ maxWidth: 860, margin: '0 auto', padding: '48px 24px 80px' }}>
        {/* Category tabs */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 40 }}>
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              style={{
                padding: '6px 16px', borderRadius: 0, fontSize: 12, cursor: 'pointer',
                border: `1px solid ${activeCategory === cat ? 'var(--ink)' : 'var(--line)'}`,
                background: activeCategory === cat ? 'var(--ink)' : 'var(--card)',
                color: activeCategory === cat ? 'var(--paper)' : 'var(--muted)',
                fontFamily: 'var(--font-mono)', letterSpacing: '0.5px',
                transition: 'all 0.1s'
              }}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* FAQ groups */}
        {filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 24px', color: 'var(--muted)' }}>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: 20, color: 'var(--ink)', marginBottom: 8 }}>No results found</div>
            <p>Try a different search term</p>
          </div>
        ) : (
          filtered.map((group, i) => (
            <div key={i} style={{ marginBottom: 48 }}>
              <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 22, marginBottom: 4, paddingBottom: 14, borderBottom: '2px solid var(--ink)', color: 'var(--ink)' }}>
                {group.category}
              </h2>
              {group.items.map((item, j) => (
                <FAQItem key={j} q={item.q} a={item.a} />
              ))}
            </div>
          ))
        )}

        {/* Still need help */}
        <div style={{ marginTop: 48, textAlign: 'center', padding: '40px', background: 'var(--card)', border: '1px solid var(--line-card)', borderRadius: 0 }}>
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 22, marginBottom: 8, color: 'var(--ink)' }}>Still have questions?</h3>
          <p style={{ color: 'var(--muted)', fontSize: 14, marginBottom: 24, lineHeight: 1.6 }}>
            Contact us at <a href="mailto:support@biomedarticles.com" style={{ color: 'var(--ink)', fontWeight: 600 }}>support@biomedarticles.com</a>
          </p>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button style={{ background: 'var(--ink)', color: 'var(--paper)', border: 'none', padding: '12px 28px', fontSize: 14, fontWeight: 700, borderRadius: 0, cursor: 'pointer' }} onClick={() => navigate('/how-it-works')}>
              How It Works →
            </button>
            <button style={{ background: 'transparent', color: 'var(--ink)', border: '1.5px solid var(--ink)', padding: '12px 28px', fontSize: 14, fontWeight: 500, borderRadius: 0, cursor: 'pointer' }} onClick={() => navigate('/register')}>
              Create free account
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}